import { createElement, useCallback } from 'react';

import { CssVarsProvider } from '@mui/joy/styles';
import { flushSync } from 'react-dom';
import { Root, createRoot } from 'react-dom/client';

import { track } from '../../services/analytics';
import {
  EVENT_TUTORIAL,
  EVNT_tutorial_screen_closed,
  EVNT_tutorial_screen_main_cta_clicked,
  EVNT_tutorial_screen_secondary_cta_clicked,
  PGV_tutorial_screen_shown,
} from '../../services/analytics/events';
import { driver } from '../../services/driverjs';
import { theme } from '../../theme';
import { brand } from '../../theme/brand';

import {
  BACK_BUTTON_TEXT,
  CONTINUE_BUTTON_TEXT,
  FINISH_BUTTON_TEXT,
  TOUR_THEME_CSS_ID,
} from './constants';
import { HomeOnboardingPopover } from './popover/HomeOnboardingPopover';
import { TOUR_THEME_CSS } from './style';
import type { OnboardingStep, TourEventProps } from './types';

export const hasVisibleOnboardingSteps = (steps: OnboardingStep[]): boolean =>
  steps.some((step) => step.show);

const tourHeaderText = (title?: string): string => (title ?? '').split(' ').slice(0, 3).join(' ');

const injectTourThemeStyle = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(TOUR_THEME_CSS_ID)) return;
  const style = document.createElement('style');
  style.id = TOUR_THEME_CSS_ID;
  style.textContent = TOUR_THEME_CSS;
  document.head.appendChild(style);
};

export const runOnboardingTour = (steps: OnboardingStep[], eventProps: TourEventProps) => {
  const visibleSteps = steps.filter((step) => step.show);
  if (visibleSteps.length === 0) return;

  injectTourThemeStyle();

  const isDarkMode = document.documentElement.getAttribute('data-joy-color-scheme') === 'dark';
  const overlayColor = isDarkMode ? brand.blur.light : brand.blur.dark;
  const overlayOpacity = isDarkMode ? 0.2 : 0.1;

  let popoverRoot: Root | null = null;
  let customContainer: HTMLDivElement | null = null;
  let interactiveTarget: HTMLElement | null = null;
  let interactiveHandler: ((event: Event) => void) | null = null;

  let lastShownIdx = -1;
  const stepEventProps = (idx: number) => {
    const step = visibleSteps[idx];
    // Per-step (number + header text) overlaid on top of the launcher-provided
    // context (platform_page, feature_name, fastpass_used, …). Order matters:
    // spread eventProps first so per-step values win for the shared keys, and
    // any extra launcher props (fastpass counts, etc.) ride along too.
    return {
      ...eventProps,
      tutorial_step_number: `Step ${idx + 1}`,
      tutorial_screen_header_text: tourHeaderText(step?.title),
    };
  };

  const detachInteractiveListener = () => {
    if (interactiveTarget && interactiveHandler) {
      interactiveTarget.removeEventListener('click', interactiveHandler);
    }
    interactiveTarget = null;
    interactiveHandler = null;
  };

  const attachInteractiveListener = (idx: number) => {
    detachInteractiveListener();
    const step = visibleSteps[idx];
    if (!step?.interactive) return;
    const target = document.querySelector(step.element);
    if (!(target instanceof HTMLElement)) return;
    const handler = () => {
      if (idx === visibleSteps.length - 1) tour.destroy();
      else tour.moveNext();
    };
    target.addEventListener('click', handler);
    interactiveTarget = target;
    interactiveHandler = handler;
  };

  // Standalone green dot pinned to the highlight's corner facing the popover —
  // independent of driver.js's arrow (which we hide), so it's never thrown off
  // by floating-ui's overflow shifting of the popover.
  let floatingDot: HTMLDivElement | null = null;
  let floatingArrow: HTMLDivElement | null = null;
  const ensureDot = (): HTMLDivElement => {
    if (!floatingDot) {
      floatingDot = document.createElement('div');
      floatingDot.className = 'calyx-tour-dot';
      document.body.appendChild(floatingDot);
    }
    return floatingDot;
  };
  const ensureArrow = (): HTMLDivElement => {
    if (!floatingArrow) {
      floatingArrow = document.createElement('div');
      floatingArrow.className = 'calyx-tour-arrow';
      document.body.appendChild(floatingArrow);
    }
    return floatingArrow;
  };
  const removeOverlayBits = () => {
    if (floatingDot && floatingDot.parentNode) floatingDot.parentNode.removeChild(floatingDot);
    if (floatingArrow && floatingArrow.parentNode)
      floatingArrow.parentNode.removeChild(floatingArrow);
    floatingDot = null;
    floatingArrow = null;
  };
  const rectsOverlap = (a: DOMRect, b: DOMRect): boolean =>
    !(a.right <= b.left || a.left >= b.right || a.bottom <= b.top || a.top >= b.bottom);

  const positionDotForStep = (idx: number) => {
    const step = visibleSteps[idx];
    if (!step) return removeOverlayBits();
    const target = document.querySelector(step.element);
    if (!(target instanceof HTMLElement)) return removeOverlayBits();
    const t = target.getBoundingClientRect();
    const popover = document.querySelector<HTMLElement>('.driver-popover');
    const p = popover?.getBoundingClientRect();
    // If after driver.js's own scrollIntoView the popover still overlaps the
    // highlight (target is taller than the viewport, for example) drop the
    // visual cues — popover-on-highlight is its own indicator.
    if (p && rectsOverlap(p, t)) return removeOverlayBits();
    const dot = ensureDot();
    const arrow = ensureArrow();
    // Decide which axis the popover sits on relative to the highlight, then
    // place the dot on the highlight's facing edge and the arrow on the
    // popover's facing edge — both aligned along the same axis so they read
    // as a single visual connection.
    let dotX = t.left + t.width / 2;
    let dotY = t.top + t.height / 2;
    let arrowX = dotX;
    let arrowY = dotY;
    let arrowSide: 'left' | 'right' | 'top' | 'bottom' = 'left';
    if (p) {
      const targetCx = t.left + t.width / 2;
      const targetCy = t.top + t.height / 2;
      const popoverCx = p.left + p.width / 2;
      const popoverCy = p.top + p.height / 2;
      const dx = popoverCx - targetCx;
      const dy = popoverCy - targetCy;
      if (Math.abs(dx) > Math.abs(dy)) {
        // Popover sits to the side of the highlight (left or right).
        const popoverIsRight = dx > 0;
        dotX = popoverIsRight ? t.right : t.left;
        dotY = Math.max(t.top + 12, Math.min(t.bottom - 12, popoverCy));
        arrowX = popoverIsRight ? p.left : p.right;
        arrowY = dotY;
        arrowSide = popoverIsRight ? 'left' : 'right';
      } else {
        // Popover sits above or below the highlight.
        const popoverIsBelow = dy > 0;
        dotY = popoverIsBelow ? t.bottom : t.top;
        dotX = Math.max(t.left + 12, Math.min(t.right - 12, popoverCx));
        arrowY = popoverIsBelow ? p.top : p.bottom;
        arrowX = dotX;
        arrowSide = popoverIsBelow ? 'top' : 'bottom';
      }
    }
    dot.style.left = `${dotX}px`;
    dot.style.top = `${dotY}px`;
    arrow.style.left = `${arrowX}px`;
    arrow.style.top = `${arrowY}px`;
    arrow.dataset.side = arrowSide;
  };
  let lastIdx = 0;
  const repositionDot = () => positionDotForStep(lastIdx);
  window.addEventListener('resize', repositionDot);
  window.addEventListener('scroll', repositionDot, true);

  const mountReact = (popover: { wrapper: HTMLElement }, idx: number) => {
    const step = visibleSteps[idx];
    if (!step) return;

    const wrapper = popover.wrapper;
    wrapper.style.padding = '0';
    wrapper.style.maxWidth = 'none';
    wrapper.style.width = 'auto';
    wrapper.style.background = 'transparent';
    wrapper.style.boxShadow = 'none';
    wrapper.style.borderRadius = '10px';
    // Hide driver.js's default popover chrome but keep the arrow — the green
    // dot is positioned independently (anchored to the highlight) so it stays
    // accurate even when floating-ui shifts the popover to fit the viewport.
    Array.from(wrapper.children).forEach((child) => {
      if (child === customContainer) return;
      const el = child as HTMLElement;
      if (el.classList.contains('driver-popover-arrow')) return;
      el.style.display = 'none';
    });

    if (customContainer && customContainer.parentNode !== wrapper) {
      wrapper.appendChild(customContainer);
    }
    if (!customContainer) {
      customContainer = document.createElement('div');
      wrapper.appendChild(customContainer);
      popoverRoot = createRoot(customContainer);
    }

    flushSync(() => {
      popoverRoot?.render(
        createElement(
          CssVarsProvider,
          { theme, defaultMode: 'light' },
          createElement(HomeOnboardingPopover, {
            title: step.title,
            body: step.body,
            customContent: step.customContent,
            stepIndex: idx,
            totalSteps: visibleSteps.length,
            interactive: step.interactive,
            onBack: () => {
              track(EVNT_tutorial_screen_secondary_cta_clicked, EVENT_TUTORIAL, {
                ...stepEventProps(idx),
                button_text: BACK_BUTTON_TEXT,
              });
              if (idx > 0) tour.movePrevious();
            },
            onContinue: () => {
              track(EVNT_tutorial_screen_main_cta_clicked, EVENT_TUTORIAL, {
                ...stepEventProps(idx),
                button_text:
                  idx === visibleSteps.length - 1 ? FINISH_BUTTON_TEXT : CONTINUE_BUTTON_TEXT,
              });
              if (idx === visibleSteps.length - 1) tour.destroy();
              else tour.moveNext();
            },
            onClose: () => {
              track(EVNT_tutorial_screen_closed, EVENT_TUTORIAL, stepEventProps(idx));
              tour.destroy();
            },
          })
        )
      );
    });
  };

  const hasInteractiveStep = visibleSteps.some((step) => step.interactive);

  const tour = driver({
    showProgress: false,
    showButtons: [],
    allowClose: !hasInteractiveStep,
    smoothScroll: false,
    overlayColor,
    overlayOpacity,
    popoverClass: 'driverjs-onboarding-theme',
    onDestroyed: () => {
      detachInteractiveListener();
      removeOverlayBits();
      window.removeEventListener('resize', repositionDot);
      window.removeEventListener('scroll', repositionDot, true);
      const root = popoverRoot;
      popoverRoot = null;
      customContainer = null;
      if (root) queueMicrotask(() => root.unmount());
    },
    onPopoverRender: (popover, { state }) => {
      const idx = state.activeIndex ?? 0;
      // PGV once per step (onPopoverRender may fire on re-render).
      if (idx !== lastShownIdx) {
        lastShownIdx = idx;
        track(PGV_tutorial_screen_shown, EVENT_TUTORIAL, stepEventProps(idx));
      }
      mountReact(popover, idx);
      attachInteractiveListener(idx);
      lastIdx = idx;
      // Position the floating dot on the next frame so the popover has settled
      // its final rect (we read it to decide which edge of the highlight to
      // anchor the dot to).
      requestAnimationFrame(() => positionDotForStep(idx));
    },
    steps: visibleSteps.map((s) => ({
      element: s.element,
      popover: { side: s.side, align: s.align ?? 'center', title: '', description: '' },
    })),
  });

  tour.drive();
};

export const useOnboardingTour = (steps: OnboardingStep[], eventProps: TourEventProps) => {
  const canShow = hasVisibleOnboardingSteps(steps);
  const start = useCallback(() => runOnboardingTour(steps, eventProps), [steps, eventProps]);
  return { canShow, start };
};
